import {
  COLOR_START_INDEX,
  HOME_COLUMNS,
  SAFE_TRACK_INDICES,
  TRACK_CELLS,
  YARD_CELLS,
  absoluteTrackIndex,
} from "@ludo/engine";
import type { PieceView, PlayerColor } from "@ludo/shared";

const CELL = 40;
const SIZE = 15 * CELL;

const COLOR_HEX: Record<PlayerColor, string> = {
  RED: "#e53935",
  BLUE: "#1e88e5",
  GREEN: "#43a047",
  YELLOW: "#fdd835",
};

const YARD_BG: Record<PlayerColor, [number, number]> = {
  RED: [0, 0],
  GREEN: [0, 9],
  YELLOW: [9, 9],
  BLUE: [9, 0],
};

const START_TO_COLOR: Record<number, PlayerColor> = Object.fromEntries(
  (Object.entries(COLOR_START_INDEX) as [PlayerColor, number][]).map(([c, i]) => [i, c]),
);

interface BoardProps {
  pieces: PieceView[];
  validMoves: string[];
  onPieceClick: (pieceId: string) => void;
  myColor: PlayerColor | null;
}

interface PlacedPiece extends PieceView {
  x: number;
  y: number;
}

/** Progress -> grid cell center coordinates. */
export function pieceCell(color: PlayerColor, progress: number, pieceIndex: number): [number, number] {
  if (progress === -1) return YARD_CELLS[color][pieceIndex % 4]!;
  if (progress >= 51) {
    if (progress === 56) return [7, 7]; // center
    return HOME_COLUMNS[color][progress - 51]!;
  }
  const abs = absoluteTrackIndex(color, progress)!;
  return TRACK_CELLS[abs]!;
}

export function Board({ pieces, validMoves, onPieceClick, myColor }: BoardProps) {
  // place pieces; stack duplicates with small offsets
  const placed: PlacedPiece[] = [];
  const cellCount = new Map<string, number>();
  for (const p of pieces) {
    const idx = Number(p.id.split("-")[1]) - 1;
    const [r, c] = pieceCell(p.color, p.progress, idx);
    const key = `${r},${c}`;
    const n = cellCount.get(key) ?? 0;
    cellCount.set(key, n + 1);
    const dx = (n % 2) * 14 - 7;
    const dy = Math.floor(n / 2) * 14 - 7;
    placed.push({ ...p, x: c * CELL + CELL / 2 + (n ? dx : 0), y: r * CELL + CELL / 2 + (n ? dy : 0) });
  }

  const yardRects = (Object.entries(YARD_BG) as [PlayerColor, [number, number]][]).map(
    ([color, [r, c]]) => (
      <g key={color}>
        <rect x={c * CELL} y={r * CELL} width={6 * CELL} height={6 * CELL} fill={COLOR_HEX[color]} opacity={0.85} rx={8} />
        <rect x={(c + 1) * CELL} y={(r + 1) * CELL} width={4 * CELL} height={4 * CELL} fill="#fff" rx={6} />
        {YARD_CELLS[color].map(([yr, yc], i) => (
          <circle key={i} cx={yc * CELL + CELL / 2} cy={yr * CELL + CELL / 2} r={CELL * 0.32} fill={COLOR_HEX[color]} opacity={0.35} />
        ))}
      </g>
    ),
  );

  const trackCells = TRACK_CELLS.map(([r, c], i) => {
    const startColor = START_TO_COLOR[i];
    const isSafe = SAFE_TRACK_INDICES.has(i);
    const fill = startColor ? COLOR_HEX[startColor] : "#ffffff";
    return (
      <g key={i}>
        <rect x={c * CELL} y={r * CELL} width={CELL} height={CELL} fill={fill} stroke="#c9d1d9" strokeWidth={1} opacity={startColor ? 0.9 : 1} />
        {isSafe && !startColor && (
          <text x={c * CELL + CELL / 2} y={r * CELL + CELL / 2 + 6} textAnchor="middle" fontSize={18} fill="#90a4ae">
            ★
          </text>
        )}
      </g>
    );
  });

  const homeCols = (Object.entries(HOME_COLUMNS) as [PlayerColor, readonly (readonly [number, number])[]][]).map(
    ([color, cells]) =>
      cells.map(([r, c], i) => (
        <rect key={`${color}-${i}`} x={c * CELL} y={r * CELL} width={CELL} height={CELL} fill={COLOR_HEX[color]} stroke="#c9d1d9" strokeWidth={1} opacity={0.85} />
      )),
  );

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="board" role="img" aria-label="Ludo board">
      <rect width={SIZE} height={SIZE} fill="#eceff1" rx={12} />
      {yardRects}
      {trackCells}
      {homeCols}
      {/* center */}
      <polygon points={`${6 * CELL},${6 * CELL} ${9 * CELL},${6 * CELL} ${7.5 * CELL},${7.5 * CELL}`} fill={COLOR_HEX.GREEN} />
      <polygon points={`${9 * CELL},${6 * CELL} ${9 * CELL},${9 * CELL} ${7.5 * CELL},${7.5 * CELL}`} fill={COLOR_HEX.YELLOW} />
      <polygon points={`${9 * CELL},${9 * CELL} ${6 * CELL},${9 * CELL} ${7.5 * CELL},${7.5 * CELL}`} fill={COLOR_HEX.BLUE} />
      <polygon points={`${6 * CELL},${9 * CELL} ${6 * CELL},${6 * CELL} ${7.5 * CELL},${7.5 * CELL}`} fill={COLOR_HEX.RED} />
      {/* pieces */}
      {placed.map((p) => {
        const movable = validMoves.includes(p.id) && p.color === myColor;
        return (
          <g
            key={p.id}
            onClick={() => movable && onPieceClick(p.id)}
            style={{ cursor: movable ? "pointer" : "default", transition: "all 300ms ease" }}
          >
            {movable && (
              <circle cx={p.x} cy={p.y} r={CELL * 0.42} fill="none" stroke="#ffffff" strokeWidth={3} className="pulse" />
            )}
            <circle
              cx={p.x}
              cy={p.y}
              r={CELL * 0.3}
              fill={COLOR_HEX[p.color]}
              stroke="#263238"
              strokeWidth={1.5}
            />
            <circle cx={p.x - 4} cy={p.y - 4} r={CELL * 0.08} fill="#ffffff" opacity={0.7} />
          </g>
        );
      })}
    </svg>
  );
}
